import { TypographyVariants } from '@mui/material/styles/createTypography';
import pxToRem from 'assets/theme/function/px-to-rem';
import colors from 'assets/theme/base/colors';
import * as React from 'react';
import { breakpoints } from 'assets/theme/base/breakpoints';

interface TypographyOptionsCustom extends TypographyVariants {
    size?: any;
    fontFamilySecond: string;
    fontWeightXBold: React.CSSProperties['fontWeight'];
}

const baseProperties = {
    fontFamily: 'Open Sans',
    fontFamilySecond: 'Lato',
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 600,
    fontWeightBold: 700,
    fontWeightXBold: 800,
    fontSize: 16,
    htmlFontSize: 16,
    pxToRem,
    fontSizeXXS: pxToRem(11),
    fontSizeXS: pxToRem(12),
    fontSizeSM: pxToRem(14),
    fontSizeMD: pxToRem(16),
    fontSizeLG: pxToRem(18),
    fontSizeXL: pxToRem(20),
    fontSize2XL: pxToRem(22),
    fontSize3XL: pxToRem(24),
    fontSize3XXL: pxToRem(26),
    fontSize4XL: pxToRem(28),
    fontSize5XL: pxToRem(30),
    fontSize6XL: pxToRem(36),
    fontSize7XL: pxToRem(48),
};

const baseHeadingProperties = {
    fontFamily: baseProperties.fontFamily,
    color: colors.common.white,
    fontWeight: baseProperties.fontWeightRegular,
    lineHeight: 'normal',
};

const typography: TypographyOptionsCustom = {
    fontFamily: baseProperties.fontFamily,
    fontWeightLight: baseProperties.fontWeightLight,
    fontWeightRegular: baseProperties.fontWeightRegular,
    fontWeightMedium: baseProperties.fontWeightMedium,
    fontWeightBold: baseProperties.fontWeightBold,
    fontFamilySecond: baseProperties.fontFamilySecond,
    fontWeightXBold: baseProperties.fontWeightXBold,
    fontSize: baseProperties.fontSize,
    htmlFontSize: baseProperties.htmlFontSize,
    pxToRem: baseProperties.pxToRem,

    h1: {
        fontSize: baseProperties.fontSize5XL,
        ...baseHeadingProperties,
    },

    h2: {
        fontSize: baseProperties.fontSize4XL,
        ...baseHeadingProperties,
        [breakpoints.down('lg')]: {
            fontSize: baseProperties.fontSize3XL,
        },

        [breakpoints.down('md')]: {
            fontSize: baseProperties.fontSize2XL,
        },

        [breakpoints.down('sm')]: {
            fontSize: baseProperties.fontSizeXL,
        },
    },

    h3: {
        fontSize: baseProperties.fontSize3XL,
        ...baseHeadingProperties,
    },

    h4: {
        fontSize: baseProperties.fontSize2XL,
        ...baseHeadingProperties,
    },

    h5: {
        fontSize: baseProperties.fontSizeXL,
        ...baseHeadingProperties,
    },

    h6: {
        fontSize: baseProperties.fontSizeLG,
        ...baseHeadingProperties,
    },

    subtitle1: {
        fontSize: baseProperties.fontSizeMD,
        ...baseHeadingProperties,
    },

    subtitle2: {
        fontSize: baseProperties.fontSizeSM,
        ...baseHeadingProperties,
    },

    body1: {
        fontSize: baseProperties.fontSizeMD,
        ...baseHeadingProperties,
    },

    body2: {
        fontSize: baseProperties.fontSizeXS,
        ...baseHeadingProperties,
    },

    button: {
        fontSize: baseProperties.fontSizeXL,
        textTransform: 'capitalize',
        ...baseHeadingProperties,
    },

    caption: {
        fontFamily: baseProperties.fontFamily,
        fontWeight: baseProperties.fontWeightXBold,
        letterSpacing: pxToRem(-0.057),
    },

    overline: {
        fontFamily: baseProperties.fontFamily,
    },

    size: {
        xxs: baseProperties.fontSizeXXS,
        xs: baseProperties.fontSizeXS,
        sm: baseProperties.fontSizeSM,
        md: baseProperties.fontSizeMD,
        lg: baseProperties.fontSizeLG,
        xl: baseProperties.fontSizeXL,
        xxl: baseProperties.fontSize2XL,
        xxxl: baseProperties.fontSize3XL,
        xxxxl: baseProperties.fontSize3XXL,
        sixXl: baseProperties.fontSize6XL,
        sevenXl: baseProperties.fontSize7XL,
    },
};

export default typography;
